import React, { useRef, useCallback, useEffect, useLayoutEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../Icon';
import { useBackClose } from '../../utils/backStack';
import { toast } from '../Toast';
import ErrorBoundary from '../ErrorBoundary';
import { apiJson } from '../../utils/api';
import { Avatar } from '../Avatar';
import { getDisplayName } from '../../utils/admin';
import { plainTextOf } from '../../utils/messageActions';
import { Message } from './Message';
import { MessageSheet } from './MessageSheet';
import { SearchPanel } from './SearchPanel';
import { SavedPanel, FilesPanel } from './SidePanels';
import { uiZoom } from '../../utils/uiScale';

// 화면 밖 이만큼 남았을 때 미리 다음 페이지를 가져온다. 끝에 닿은 뒤에 요청하면
// 사용자는 매번 빈 화면을 보며 기다리게 된다 → 도착하기 전에 채워 둔다.
const PREFETCH_PX = 700;
const ANCHOR_HOLD_MS = 1500;  // 위에 붙인 메시지의 사진이 늦게 떠서 높이가 늘어나는 동안 보던 위치를 붙잡아 둔다
const STICK_BOTTOM_PX = 150; // 바닥 근처면 새 메시지 도착 시 자동으로 따라 내려감
const GROUP_MS = 5 * 60 * 1000;
const READ_COUNT_POLL_MS = 30000;

const distanceToBottom = (el) => el.scrollHeight - el.scrollTop - el.clientHeight;
const dayKey = (ts) => new Date(ts).toLocaleDateString();

// 특정 메시지가 화면 안에서 차지하고 있던 위치(컨테이너 기준 y)
const relativeTop = (el, msgId) => {
    const node = el.querySelector(`[data-message-id='${msgId}']`);
    // 화면 확대(zoom) 중이면 getBoundingClientRect 는 화면 픽셀이라 scrollTop 단위로 바꾼다
    return node ? (node.getBoundingClientRect().top - el.getBoundingClientRect().top) / uiZoom() : null;
};

// 이전 메시지를 위에 붙인 뒤, 기준 메시지가 원래 있던 자리에 계속 머물게 한다.
// scrollHeight 차이만 한 번 더해 주는 방식은 사진·동영상이 나중에 로드되며 높이가 늘어나면 어긋난다.
// 그래서 잠깐(ANCHOR_HOLD_MS) 동안 계속 맞춰 주고, 사용자가 직접 스크롤하면 즉시 손을 뗀다.
const holdAnchor = (el, msgId, wantTop) => {
    const until = Date.now() + ANCHOR_HOLD_MS;
    let expected = null;
    const step = () => {
        if (!el.isConnected || Date.now() > until) return;
        if (expected !== null && Math.abs(el.scrollTop - expected) > 2) return; // 사용자가 스크롤했다 → 그만둔다
        const now = relativeTop(el, msgId);
        if (now !== null) {
            const delta = now - wantTop;
            if (Math.abs(delta) > 1) el.scrollTop += delta;
        }
        expected = el.scrollTop;
        requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
};

const NOTIFY_OPTIONS = [
    { value: 'all', icon: 'bellActive', label: '모든 메시지', desc: '새 메시지마다 알림' },
    { value: 'mentions', icon: 'at', label: '멘션만', desc: '나를 부르거나 @everyone 일 때만' },
    { value: 'none', icon: 'bellOff', label: '알림 끄기', desc: '이 채널은 알림·소리 없음' },
];

// 전송 중이거나 전송에 실패한 내 메시지
const PendingMessage = ({ item, me, onRetry, onDiscard }) => (
    <div className={`message-container pending${item.status === 'failed' ? ' failed' : ''}`}>
        <div className="message-swipe">
            <Avatar userId={me.userId} name={getDisplayName(me.username)} />
            <div className="message-content">
                <div className="message-header"><strong>{getDisplayName(me.username)}</strong></div>
                <div className="message-body pending-body">{plainTextOf({ message: item.message }).slice(0, 500)}</div>
                <div className="pending-status">
                    {item.status === 'failed' ? (
                        <>
                            <span className="pending-error">⚠️ {item.error || '전송하지 못했습니다.'}</span>
                            <button onClick={() => onRetry(item)}>다시 보내기</button>
                            <button className="ghost" onClick={() => onDiscard(item)}>삭제</button>
                        </>
                    ) : <span className="pending-sending">전송 중…</span>}
                </div>
            </div>
        </div>
    </div>
);

export function ChatWindow({
    messages, generation, isAdmin, clientData, channelId, channels = [],
    hasOlder, hasNewer, loadingOlder, loadingNewer, onLoadOlder, onLoadNewer, onJumpToLatest, loadError,
    targetMsgId, awaitingTarget, onTargetShown,
    onSearch, onSearchUsers, onChannelSelect, onNavigateToMsg,
    setReply, onDelete, onRollback, onMediaClick, onEdit, onMentionUser, sendReact, onEditingChange,
    lastReadId = 0, onRead, pending = [], onRetryPending, onDiscardPending,
    savedKeys, onToggleSave, onRemoveSaved, editRequest,
    isAnnouncement, notifyLevel, defaultNotifyLevel = 'mentions', onChangeNotify, toolbarSlot,
}) {
    const chatRef = useRef(null);
    const [panel, setPanel] = useState(null); // 'search' | 'saved' | 'files' | null
    const [notifyOpen, setNotifyOpen] = useState(false);
    useBackClose(!!panel, () => setPanel(null));
    useBackClose(notifyOpen, () => setNotifyOpen(false));
    const [linkCopied, setLinkCopied] = useState(false);
    const [showJumpButton, setShowJumpButton] = useState(false);
    const [newCount, setNewCount] = useState(0);
    const [sheet, setSheet] = useState(null);
    const [readCounts, setReadCounts] = useState({});
    // "여기까지 읽음" 선은 채널을 연 시점의 읽음 위치로 고정 (읽는 동안 선이 따라 내려가지 않게)
    const [readLineId, setReadLineId] = useState(lastReadId);
    const lastReadRef = useRef(lastReadId);
    lastReadRef.current = lastReadId;
    const toolbarRef = useRef(null);

    // 스크롤 보정용 기록
    const scrollState = useRef({ generation: -1, lastId: 0, anchorId: 0, anchorTop: 0, appendingPage: false });

    const visible = useMemo(() => (isAdmin ? messages : messages.filter(m => !m.is_deleted)), [messages, isAdmin]);
    const lastId = visible.length ? visible[visible.length - 1].id : 0;
    // 관찰자(IntersectionObserver) 안에서 최신 목록을 읽기 위한 참조 — 목록이 바뀔 때마다 관찰자를 다시 만들지 않으려고 쓴다
    const visibleRef = useRef(visible);
    visibleRef.current = visible;
    const olderFailed = !!loadError?.older;
    const newerFailed = !!loadError?.newer;

    useEffect(() => { setReadLineId(lastReadRef.current); setNewCount(0); setPanel(p => (p === 'files' ? null : p)); setReadCounts({}); }, [channelId]);

    // ── 스크롤 위치 관리 ────────────────────────────────────
    useLayoutEffect(() => {
        const el = chatRef.current;
        if (!el) return;
        const s = scrollState.current;

        if (s.generation !== generation) {
            // 목록 교체(첫 로드·위치 이동·최신으로 이동): 이동 대상이 있으면 그쪽에서 처리
            s.generation = generation;
            s.lastId = lastId;
            s.anchorId = 0;
            s.appendingPage = false;
            setNewCount(0);
            if (targetMsgId || awaitingTarget) return; // 메시지 링크·검색 이동 중이면 이동 쪽에서 스크롤
            const savedLastRead = lastReadRef.current;
            const firstUnread = savedLastRead ? visible.find(m => m.id > savedLastRead && m.userId !== clientData.userId) : null;
            const unreadEl = firstUnread && el.querySelector(`[data-message-id='${firstUnread.id}']`);
            if (unreadEl && !hasNewer) unreadEl.scrollIntoView({ block: 'center' });
            else el.scrollTop = el.scrollHeight;
            return;
        }

        if (s.anchorId) {
            // 이전 메시지를 위에 붙였으면 보고 있던 메시지가 그 자리에 그대로 있게 한다
            const anchorId = s.anchorId, anchorTop = s.anchorTop;
            s.anchorId = 0;
            const now = relativeTop(el, anchorId);
            if (now !== null) el.scrollTop += now - anchorTop;
            holdAnchor(el, anchorId, anchorTop);
        } else if (lastId > s.lastId) {
            const newest = visible[visible.length - 1];
            if (s.appendingPage) s.appendingPage = false; // 이후 페이지를 붙인 경우엔 위치를 그대로 둔다
            else if (newest?.userId === clientData.userId || distanceToBottom(el) < STICK_BOTTOM_PX + 200) el.scrollTop = el.scrollHeight;
            else {
                // 위쪽을 보고 있는 동안 도착한 다른 사람 메시지 수 → "새 메시지 N개" 버튼
                const arrived = visible.filter(m => m.id > s.lastId && m.userId !== clientData.userId).length;
                if (arrived) setNewCount(c => c + arrived);
            }
        }
        s.lastId = lastId;
    }, [generation, lastId, visible, channelId, clientData.userId, targetMsgId, awaitingTarget, hasNewer]);

    // 내가 보낸 메시지(전송 중 표시)가 추가되면 바닥으로
    useLayoutEffect(() => {
        const el = chatRef.current;
        if (el && pending.length) el.scrollTop = el.scrollHeight;
    }, [pending.length]);

    // 이동 대상 메시지로 스크롤 + 강조 (이미지 로딩 등으로 늦게 그려질 수 있어 몇 번 재시도)
    useEffect(() => {
        if (!targetMsgId) return undefined;
        let tries = 0, timer = null;
        const attempt = () => {
            const node = chatRef.current?.querySelector(`[data-message-id='${targetMsgId}']`);
            if (node) {
                node.scrollIntoView({ block: 'center' });
                node.classList.add('highlight');
                setTimeout(() => node.classList.remove('highlight'), 2000);
                onTargetShown?.();
            } else if (tries++ < 15) {
                timer = setTimeout(attempt, 150);
            }
        };
        attempt();
        return () => clearTimeout(timer);
    }, [targetMsgId, generation, onTargetShown]);

    // 목록 위/아래 끝의 표시(sentinel)가 화면에 들어오면 추가 로드
    // 목록 교체 직후에는 스크롤 위치가 아직 잡히지 않았으므로 잠시 대기(armed)했다가 감시한다.
    const topSentinel = useRef(null);
    const bottomSentinel = useRef(null);
    const [armed, setArmed] = useState(false);
    useEffect(() => {
        setArmed(false);
        const timer = setTimeout(() => setArmed(true), 400);
        return () => clearTimeout(timer);
    }, [generation, channelId]);

    useEffect(() => {
        const root = chatRef.current;
        if (!root || !armed) return undefined;
        const observer = new IntersectionObserver((entries) => {
            const s = scrollState.current;
            for (const entry of entries) {
                if (!entry.isIntersecting) continue;
                if (entry.target === topSentinel.current && hasOlder && !loadingOlder && !olderFailed) {
                    const first = visibleRef.current[0];
                    if (first) { s.anchorId = first.id; s.anchorTop = relativeTop(root, first.id) ?? 0; }
                    onLoadOlder();
                }
                if (entry.target === bottomSentinel.current && hasNewer && !loadingNewer) {
                    s.appendingPage = true;
                    onLoadNewer();
                }
            }
        }, { root, rootMargin: `${PREFETCH_PX}px 0px` });
        if (topSentinel.current) observer.observe(topSentinel.current);
        if (bottomSentinel.current) observer.observe(bottomSentinel.current);
        return () => observer.disconnect();
    }, [armed, hasOlder, hasNewer, loadingOlder, loadingNewer, olderFailed, onLoadOlder, onLoadNewer]);

    // 스크롤 시: 최신으로 이동 버튼 표시 + 읽음 위치 기록 (앱이 화면에 보일 때만 읽은 것으로 친다)
    useEffect(() => {
        const el = chatRef.current;
        if (!el) return undefined;
        const onScroll = () => {
            const nearBottom = distanceToBottom(el) < STICK_BOTTOM_PX;
            setShowJumpButton(hasNewer || distanceToBottom(el) > el.clientHeight * 2);
            if (nearBottom && !hasNewer) setNewCount(0);
            if (document.visibilityState !== 'visible') return;

            const box = el.getBoundingClientRect();
            let lastVisible = 0;
            el.querySelectorAll('[data-message-id]').forEach(node => {
                const r = node.getBoundingClientRect();
                const id = Number(node.dataset.messageId) || 0;
                // 끝이 보이거나, 화면보다 긴 메시지(큰 사진·긴 글)는 시작이 보이면 읽은 것으로 친다.
                // 예전엔 "통째로 화면 안"일 때만 쳐서, 맨 아래가 긴 메시지면 끝내 읽음 처리되지 않았다.
                const bottomSeen = r.bottom <= box.bottom + 100 && r.bottom > box.top;
                const topSeen = r.top >= box.top - 1 && r.top < box.bottom;
                if (bottomSeen || topSeen) lastVisible = Math.max(lastVisible, id);
            });
            // 맨 아래까지 내려와 있고 더 받을 최신 메시지가 없으면 불러온 것은 전부 읽은 것이다
            if (nearBottom && !hasNewer) {
                el.querySelectorAll('[data-message-id]').forEach(node => { lastVisible = Math.max(lastVisible, Number(node.dataset.messageId) || 0); });
            }
            if (lastVisible > lastReadRef.current) onRead?.(lastVisible);
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        document.addEventListener('visibilitychange', onScroll);
        onScroll();
        return () => { el.removeEventListener('scroll', onScroll); document.removeEventListener('visibilitychange', onScroll); };
    }, [channelId, hasNewer, onRead, lastId]);

    // 공지 채널: 최근 메시지별 읽은 사람 수
    useEffect(() => {
        if (!isAnnouncement || !visible.length) return undefined;
        let alive = true;
        const ids = visible.filter(m => !m.is_deleted).slice(-30).map(m => m.id);
        const load = () => apiJson(`/api/channels/${channelId}/read-counts?ids=${ids.join(',')}`)
            .then(d => alive && setReadCounts(d.counts || {}))
            .catch(() => {});
        load();
        const timer = setInterval(load, READ_COUNT_POLL_MS);
        return () => { alive = false; clearInterval(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAnnouncement, channelId, lastId]);

    // 이미지가 늦게 로드되며 높이가 늘어날 때 바닥에 붙어 있던 경우만 따라 내려감.
    // "내 메시지면 따라 내려감"은 **방금 보낸 최근 메시지**에만 적용한다. (2026-09-29)
    // 예전엔 이전 메시지를 불러올 때 위쪽에 붙은 "내 옛 사진"이 로드되기만 해도 맨 아래로 끌려 내려갔다
    // — 태블릿 가로처럼 한 화면에 사진이 많이 보일수록 자주 일어났다.
    const handleImageLoad = useCallback((msgUserId, msgId) => {
        const el = chatRef.current;
        if (!el || hasNewer) return;
        const list = visibleRef.current;
        const recentFrom = list.length > 3 ? list[list.length - 3].id : 0;
        const isMyRecent = msgUserId === clientData.userId && !!msgId && msgId >= recentFrom;
        if (isMyRecent || distanceToBottom(el) < STICK_BOTTOM_PX + 350) el.scrollTop = el.scrollHeight;
    }, [clientData.userId, hasNewer]);

    // 코드 블록 복사/접기, #채널 칩 클릭 (메시지 HTML 안의 요소라 이벤트 위임)
    useEffect(() => {
        const el = chatRef.current;
        if (!el) return undefined;
        const onClick = (e) => {
            const t = e.target;
            const code = t.closest('.code-block-container')?.querySelector('code');
            if (t.classList.contains('btn-select-all') && code) {
                const range = document.createRange(); range.selectNodeContents(code);
                const sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(range);
            }
            if (t.classList.contains('btn-copy-code') && code) {
                navigator.clipboard.writeText(code.innerText)
                    .then(() => { t.innerText = '완료!'; setTimeout(() => { t.innerText = '복사'; }, 2000); })
                    .catch(() => toast.error('복사에 실패했습니다.'));
            }
            if (t.classList.contains('btn-toggle-code')) {
                const box = t.closest('.code-block-container');
                const collapsed = box.classList.toggle('collapsed');
                box.classList.toggle('open', !collapsed);
                t.innerText = collapsed ? '펼치기' : '접기';
            }
            if (t.classList.contains('channel-mention-chip')) {
                const id = Number(t.dataset.channelId);
                if (id) onChannelSelect?.(id);
            }
        };
        el.addEventListener('click', onClick);
        return () => el.removeEventListener('click', onClick);
    }, [onChannelSelect]);

    // 알림 설정 팝오버: 바깥 누르면 닫기
    useEffect(() => {
        if (!notifyOpen) return undefined;
        const close = (e) => { if (!toolbarRef.current?.contains(e.target)) setNotifyOpen(false); };
        document.addEventListener('mousedown', close);
        document.addEventListener('touchstart', close, { passive: true });
        return () => { document.removeEventListener('mousedown', close); document.removeEventListener('touchstart', close); };
    }, [notifyOpen]);

    const onCopyLink = useCallback(() => { setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000); }, []);
    const scrollToMsg = useCallback((msgId) => {
        requestAnimationFrame(() => {
            const el = chatRef.current;
            const node = el?.querySelector(`[data-message-id='${msgId}']`);
            if (!node) return;
            const overflow = (node.getBoundingClientRect().bottom - el.getBoundingClientRect().bottom) / uiZoom();
            if (overflow > -60) el.scrollBy({ top: overflow + 80, behavior: 'smooth' });
        });
    }, []);
    const selectAndClose = useCallback((chId, msgId) => { setPanel(null); onNavigateToMsg(chId, msgId); }, [onNavigateToMsg]);
    const openSheet = useCallback((payload) => setSheet(payload), []);
    const closeSheet = useCallback(() => setSheet(null), []);

    const lastVisibleId = visible.length ? visible[visible.length - 1].id : 0;
    const effectiveNotify = notifyLevel || defaultNotifyLevel;
    const notifyIcon = NOTIFY_OPTIONS.find(o => o.value === effectiveNotify)?.icon || 'bell';
    const togglePanel = (name) => setPanel(p => (p === name ? null : name));

    // 도구 버튼은 채널 제목 줄 오른쪽 칸(toolbarSlot)에 그린다
    const toolbar = (
            <div className="chat-toolbar" ref={toolbarRef}>
                {onChangeNotify && (
                    <button className={`chat-tool-btn${notifyOpen ? ' active' : ''}${effectiveNotify === 'none' ? ' muted' : ''}`} onClick={() => setNotifyOpen(v => !v)} title="이 채널 알림 설정" aria-label="채널 알림 설정" aria-expanded={notifyOpen}>
                        {effectiveNotify === 'mentions' ? <span className="at-icon">@</span> : <Icon name={notifyIcon} />}
                    </button>
                )}
                <button className={`chat-tool-btn${panel === 'files' ? ' active' : ''}`} onClick={() => togglePanel('files')} title="채널 파일 모아보기" aria-label="채널 파일" aria-pressed={panel === 'files'}><Icon name="folder" /></button>
                {onToggleSave && <button className={`chat-tool-btn${panel === 'saved' ? ' active' : ''}`} onClick={() => togglePanel('saved')} title="저장한 메시지" aria-label="저장한 메시지" aria-pressed={panel === 'saved'}><Icon name="bookmark" /></button>}
                <button className={`chat-tool-btn${panel === 'search' ? ' active' : ''}`} onClick={() => togglePanel('search')} title="메시지 검색" aria-label="메시지 검색" aria-pressed={panel === 'search'}><Icon name="search" /></button>
                {notifyOpen && (
                    <div className="notify-menu" role="menu">
                        <div className="nav-popover-title">이 채널 알림</div>
                        {NOTIFY_OPTIONS.map(opt => (
                            <button key={opt.value} role="menuitemradio" aria-checked={effectiveNotify === opt.value}
                                className={`notify-option${effectiveNotify === opt.value ? ' selected' : ''}`}
                                onClick={() => { setNotifyOpen(false); onChangeNotify(opt.value === defaultNotifyLevel ? null : opt.value); }}>
                                <span className="notify-option-icon">{opt.value === 'mentions' ? '@' : <Icon name={opt.icon} size={18} />}</span>
                                <span className="notify-option-text"><b>{opt.label}{opt.value === defaultNotifyLevel ? ' (기본)' : ''}</b><small>{opt.desc}</small></span>
                                {effectiveNotify === opt.value && <span className="notify-check">✓</span>}
                            </button>
                        ))}
                    </div>
                )}
            </div>
    );

    return (
        <>
            {toolbarSlot ? createPortal(toolbar, toolbarSlot) : toolbar}

            {panel === 'search' && (
                <SearchPanel channelId={channelId} channels={channels} onSearch={onSearch} onSearchUsers={onSearchUsers}
                    onSelectResult={selectAndClose} onClose={() => setPanel(null)} />
            )}
            {panel === 'saved' && <SavedPanel onSelect={selectAndClose} onRemove={onRemoveSaved} onClose={() => setPanel(null)} />}
            {panel === 'files' && <FilesPanel channelId={channelId} onSelect={selectAndClose} onMediaClick={onMediaClick} onClose={() => setPanel(null)} />}

            <div id="chat" ref={chatRef}>
                {linkCopied && <div className="link-toast">링크 복사됨 ✅</div>}
                <div ref={topSentinel} className="list-sentinel" aria-hidden="true" />
                {/* 위쪽 안내는 한 칸(load-more-slot)을 항상 차지한다. 글자가 나타났다 사라지며 높이가 바뀌면
                    이전 메시지를 붙이는 순간 화면이 튄다. */}
                <div className="load-more-slot">
                    {olderFailed ? (
                        <div className="load-more-indicator failed">
                            ⚠️ 이전 메시지를 불러오지 못했습니다.
                            <button className="load-retry-btn" onClick={onLoadOlder}>다시 시도</button>
                        </div>
                    ) : loadingOlder ? (
                        <div className="load-more-indicator"><span className="load-spinner" aria-hidden="true" />이전 메시지를 불러오는 중</div>
                    ) : hasOlder ? (
                        <div className="load-more-indicator clickable" onClick={onLoadOlder}>↑ 이전 메시지 더 보기</div>
                    ) : visible.length ? (
                        <div className="load-more-indicator start-of-history">대화의 시작</div>
                    ) : null}
                </div>
                {visible.map((msg, index) => {
                    const prev = visible[index - 1];
                    const next = visible[index + 1];
                    const newDay = !prev || dayKey(msg.timestamp) !== dayKey(prev.timestamp);
                    const isGrouped = !!prev && !newDay && !prev.is_deleted && !msg.is_deleted && prev.userId === msg.userId
                        && new Date(msg.timestamp) - new Date(prev.timestamp) < GROUP_MS;
                    const showReadLine = readLineId > 0 && msg.id === readLineId && lastVisibleId > readLineId && next && next.userId !== clientData.userId;
                    return (
                        <React.Fragment key={msg.id}>
                            {newDay && <div className="date-separator"><span>{dayKey(msg.timestamp)}</span></div>}
                            <ErrorBoundary>
                                <Message msg={msg} isGrouped={isGrouped} clientData={clientData} isAdmin={isAdmin} setReply={setReply}
                                    onDelete={onDelete} onRollback={onRollback} onMediaClick={onMediaClick} onImageLoad={handleImageLoad}
                                    channelId={channelId} onNavigateToMsg={onNavigateToMsg} onEdit={onEdit} onMentionUser={onMentionUser}
                                    onCopyLink={onCopyLink} sendReact={sendReact} onEditingChange={onEditingChange} onScrollToMsg={scrollToMsg}
                                    isSaved={!!savedKeys?.has(`${channelId}:${msg.id}`)} onToggleSave={onToggleSave}
                                    autoEditNonce={editRequest?.msgId === msg.id ? editRequest.nonce : 0}
                                    readCount={isAnnouncement ? readCounts[msg.id] : undefined} onOpenSheet={openSheet} />
                            </ErrorBoundary>
                            {showReadLine && <div className="read-line"><span>여기까지 읽음</span></div>}
                        </React.Fragment>
                    );
                })}
                {newerFailed && (
                    <div className="load-more-indicator failed">
                        ⚠️ 이후 메시지를 불러오지 못했습니다.
                        <button className="load-retry-btn" onClick={onLoadNewer}>다시 시도</button>
                    </div>
                )}
                {!newerFailed && loadingNewer && <div className="load-more-indicator"><span className="load-spinner" aria-hidden="true" />이후 메시지를 불러오는 중</div>}
                {!newerFailed && !loadingNewer && hasNewer && <div className="load-more-indicator clickable" onClick={onLoadNewer}>↓ 이후 메시지 더 보기</div>}
                {!hasNewer && pending.map(item => (
                    <PendingMessage key={item.clientId} item={item} me={clientData} onRetry={onRetryPending} onDiscard={onDiscardPending} />
                ))}
                <div ref={bottomSentinel} className="list-sentinel" aria-hidden="true" />
            </div>
            {(showJumpButton || newCount > 0) && (
                <button className={`jump-latest-btn${newCount > 0 ? ' has-new' : ''}`} onClick={() => {
                    setNewCount(0);
                    if (hasNewer) return onJumpToLatest();
                    setShowJumpButton(false);
                    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
                }}>{newCount > 0 ? `새 메시지 ${newCount > 99 ? '99+' : newCount}개 ↓` : '↓ 최신 메시지로 이동'}</button>
            )}
            {sheet && <MessageSheet msg={sheet.msg} myUsername={clientData.username} actions={sheet.actions} onClose={closeSheet} />}
        </>
    );
}
